<template>
  <div class="feedback-call-page">
    <CCard class="feedback-shell border-0 shadow-sm">
      <CCardHeader class="feedback-header d-flex flex-wrap justify-content-between align-items-center">
        <div class="mr-3 mb-2 mb-md-0">
          <div class="feedback-eyebrow text-uppercase small">CRM & Calls</div>
          <strong class="h4 mb-1 d-block">Feedback Call</strong>
          <div class="small text-muted">Production-style report with a cleaner OMS workflow</div>
        </div>

        <div class="feedback-actions d-flex flex-wrap align-items-end">
          <CInput
            class="filter-input mr-md-2 mb-2 mb-md-0"
            label="Start Day"
            type="number"
            min="0"
            v-model.number="filters.startDay"
          />
          <CInput
            class="filter-input mr-md-2 mb-2 mb-md-0"
            label="End Day"
            type="number"
            min="0"
            v-model.number="filters.endDay"
          />
          <CButton color="primary" class="mr-2 mb-2 mb-md-0" :disabled="isLoading" @click="searchRows">
            {{ isLoading ? 'Searching...' : 'Search' }}
          </CButton>
          <CButton
            color="success"
            variant="outline"
            class="mb-2 mb-md-0"
            :disabled="isDownloading"
            @click="downloadRows"
          >
            {{ isDownloading ? 'Preparing...' : 'Download' }}
          </CButton>
        </div>
      </CCardHeader>

      <CCardBody>
        <CAlert v-if="errorMessage" color="danger" show class="mb-3">
          {{ errorMessage }}
        </CAlert>

        <CRow class="mb-3">
          <CCol md="4" sm="6" class="mb-3 mb-md-0">
            <CCard class="stat-card stat-card-total border-0 h-100">
              <CCardBody class="py-3">
                <div class="stat-label text-uppercase small">Rows</div>
                <div class="stat-value h4 mb-0">{{ rows.length }}</div>
              </CCardBody>
            </CCard>
          </CCol>
          <CCol md="4" sm="6" class="mb-3 mb-md-0">
            <CCard class="stat-card stat-card-days border-0 h-100">
              <CCardBody class="py-3">
                <div class="stat-label text-uppercase small">Range</div>
                <div class="stat-value h4 mb-0">{{ rangeLabel }}</div>
              </CCardBody>
            </CCard>
          </CCol>
          <CCol md="4" sm="12">
            <CCard class="stat-card stat-card-refresh border-0 h-100">
              <CCardBody class="py-3">
                <div class="stat-label text-uppercase small">Last Refresh</div>
                <div class="stat-value h6 mb-0">{{ lastRefreshedLabel }}</div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        <div v-if="isLoading" class="text-center py-5 text-muted">Loading feedback calls...</div>

        <div v-else-if="!rows.length" class="empty-state text-center py-5">
          <div class="h5 mb-2">No feedback calls found</div>
          <div class="text-muted">Adjust your day range and try again.</div>
        </div>

        <div v-else class="table-responsive feedback-table-wrap">
          <table class="table table-hover table-sm align-middle mb-0 feedback-table">
            <thead>
              <tr>
                <th>Contact Name</th>
                <th>Contact Phone</th>
                <th>Order Date</th>
                <th>State</th>
                <th>District</th>
                <th>Post Office</th>
                <th>Pincode</th>
                <th>Days</th>
                <th>Caller</th>
                <th>Last Call By</th>
                <th>Used Product</th>
                <th>Comments</th>
                <th>Crops</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in rows" :key="row.contact_phone + '-' + row.order_date + '-' + index">
                <td>{{ row.contact_name || '-' }}</td>
                <td>{{ row.contact_phone || '-' }}</td>
                <td>{{ row.order_date || '-' }}</td>
                <td>{{ row.state || '-' }}</td>
                <td>{{ row.district || '-' }}</td>
                <td>{{ row.post_office || '-' }}</td>
                <td>{{ row.pincode || '-' }}</td>
                <td>{{ row.days || '-' }}</td>
                <td>{{ row.caller || '-' }}</td>
                <td>{{ row.last_call_by || '-' }}</td>
                <td class="long-cell">{{ row.used_product || '-' }}</td>
                <td class="long-cell">{{ row.comments || '-' }}</td>
                <td class="long-cell">{{ row.crops || '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'FeedbackCall',
  data () {
    return {
      filters: {
        startDay: 90,
        endDay: 90
      },
      rows: [],
      isLoading: false,
      isDownloading: false,
      errorMessage: '',
      lastRefreshedAt: null
    }
  },
  computed: {
    rangeLabel () {
      return `${this.filters.startDay}d to ${this.filters.endDay}d`
    },
    lastRefreshedLabel () {
      return this.lastRefreshedAt ? this.lastRefreshedAt.toLocaleString() : 'Not fetched yet'
    }
  },
  created () {
    this.searchRows()
  },
  methods: {
    buildPayload (type) {
      return {
        start_day: Number(this.filters.startDay),
        end_day: Number(this.filters.endDay),
        type
      }
    },
    normalizeRows (responseData) {
      if (!Array.isArray(responseData)) {
        return []
      }

      return responseData.map((item) => ({
        contact_name: item?.contact_name || '',
        contact_phone: item?.contact_phone || '',
        order_date: item?.order_date || '',
        state: item?.state || '',
        district: item?.district || '',
        post_office: item?.post_office || '',
        pincode: item?.pincode || '',
        days: item?.days !== undefined && item?.days !== null ? String(item.days) : '',
        caller: item?.caller || '',
        last_call_by: item?.last_call_by || '',
        used_product: item?.used_product || '',
        comments: item?.comments || '',
        crops: item?.crops || ''
      }))
    },
    validateFilters () {
      if (Number.isNaN(Number(this.filters.startDay)) || Number(this.filters.startDay) < 0) {
        this.errorMessage = 'Start Day must be zero or greater.'
        return false
      }

      if (Number.isNaN(Number(this.filters.endDay)) || Number(this.filters.endDay) < 0) {
        this.errorMessage = 'End Day must be zero or greater.'
        return false
      }

      return true
    },
    extractErrorMessage (error, fallback) {
      const validationErrors = error?.response?.data?.error
      if (validationErrors && typeof validationErrors === 'object') {
        const firstKey = Object.keys(validationErrors)[0]
        if (firstKey && Array.isArray(validationErrors[firstKey]) && validationErrors[firstKey][0]) {
          return validationErrors[firstKey][0]
        }
      }

      return error?.response?.data?.message || fallback
    },
    searchRows () {
      this.errorMessage = ''
      if (!this.validateFilters()) {
        return
      }

      this.isLoading = true

      axios.post(
        `${this.$apiAdress}/api/calls_queue/feedback_calls_list?token=${localStorage.getItem('api_token')}`,
        this.buildPayload('search')
      )
        .then((response) => {
          this.rows = this.normalizeRows(response.data)
          this.lastRefreshedAt = new Date()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.extractErrorMessage(error, 'Unable to fetch feedback calls right now.')
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    downloadRows () {
      this.errorMessage = ''
      if (!this.validateFilters()) {
        return
      }

      this.isDownloading = true

      axios({
        method: 'post',
        url: `${this.$apiAdress}/api/calls_queue/feedback_calls_list?token=${localStorage.getItem('api_token')}`,
        data: this.buildPayload('download'),
        responseType: 'arraybuffer'
      })
        .then((response) => {
          const contentDisposition = response.headers['content-disposition'] || ''
          const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i)
          const fileName = filenameMatch && filenameMatch[1] ? filenameMatch[1] : 'feedbackcalls.xlsx'

          const blobUrl = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = blobUrl
          link.setAttribute('download', fileName)
          document.body.appendChild(link)
          link.click()
          link.remove()
          window.URL.revokeObjectURL(blobUrl)
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.extractErrorMessage(error, 'Unable to download feedback calls.')
        })
        .finally(() => {
          this.isDownloading = false
        })
    }
  }
}
</script>

<style scoped>
.feedback-call-page {
  background: linear-gradient(180deg, #f5f8fd 0%, #eef3fa 100%);
}

.feedback-shell {
  border-radius: 16px;
}

.feedback-header {
  background: linear-gradient(135deg, #ffffff 0%, #f6f9ff 100%);
}

.feedback-eyebrow {
  letter-spacing: 0.08em;
  color: #66748a;
  font-weight: 700;
}

.feedback-actions {
  gap: 0.5rem;
}

.filter-input {
  min-width: 140px;
  margin-bottom: 0;
}

.stat-card {
  border-radius: 14px;
  border-left: 4px solid transparent;
}

.stat-card-total {
  border-left-color: #3f51b5;
  background: linear-gradient(140deg, #ffffff 40%, #eef1ff 100%);
}

.stat-card-days {
  border-left-color: #0097a7;
  background: linear-gradient(140deg, #ffffff 40%, #e9fbff 100%);
}

.stat-card-refresh {
  border-left-color: #1b8f5a;
  background: linear-gradient(140deg, #ffffff 40%, #eafaf1 100%);
}

.stat-label {
  color: #6b778d;
  letter-spacing: 0.06em;
  font-weight: 700;
}

.stat-value {
  color: #18233a;
  font-weight: 700;
}

.feedback-table-wrap {
  border: 1px solid #e7edf5;
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
}

.feedback-table thead th {
  background: #f8fbff;
  color: #4a5872;
  font-size: 0.76rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-top: 0;
  white-space: nowrap;
}

.feedback-table td {
  white-space: nowrap;
}

.feedback-table .long-cell {
  min-width: 220px;
  white-space: normal;
}

.empty-state {
  border: 1px dashed #d7dfeb;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.72);
}

@media (max-width: 768px) {
  .filter-input {
    min-width: 100%;
  }
}
</style>
